import { useEffect, useState } from 'react';
import { rolesApi } from './rolesApi';
import type { PermissionCatalogModule } from './roleTypes';

export interface PermissionCatalogState {
  catalog: PermissionCatalogModule[];
  loading: boolean;
}

/**
 * The server's permission catalog, grouped by module.
 *
 * Every surface that renders permissions needs it — the role editor, the create-user form,
 * and the user profile — so the fetch and its abort live here once. Three inline copies of
 * the same request is where the three would start disagreeing about failure handling.
 *
 * The catalog is derived from the code-side registry and changes only on deploy, so a
 * failure resolves to an empty list: the caller's own empty state is a better answer than
 * an error banner on a page whose real subject loaded fine.
 */
export function usePermissionCatalog(enabled = true): PermissionCatalogState {
  const [catalog, setCatalog] = useState<PermissionCatalogModule[]>([]);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setCatalog([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    rolesApi
      .permissionCatalog(controller.signal)
      .then(setCatalog)
      .catch(() => {
        if (!controller.signal.aborted) setCatalog([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [enabled]);

  return { catalog, loading };
}
